import { Slider } from "@components/inputs";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/popovers/dialog";
import {
  ArrowTrendingUpIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import {
  XMarkIcon as XMarkIconOutline,
  CheckIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { formatPrice, joinClassNames } from "@utils/helpers";
import { AnimatePresence, Variants, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import LoadingSpinner from "@components/loadingSpinner";
import { ShoppingCartIcon } from "@heroicons/react/20/solid";
import { PriceStatus } from "@utils/enums";
import { FormattedPrice } from "@features/eventDisplayGA/types";
import { Badge, Button, Card, Text } from "@components/theming";
import { useLocale, useTranslations } from "next-intl";
import { useIsShopsite } from "@context/DomainContext";
import { format, parseISO } from "date-fns";

interface Props {
  /**
   * Formatted Price
   */
  price: FormattedPrice;

  /**
   * Loading state when buying tickets
   */
  priceLoading: {
    id: number;
    status: "loading" | "success" | "failure";
  } | null;

  /**
   * quantity of the current ticket bought
   */
  quantity?: string;
  /**
   * quantity of the previous ticket bought
   */
  prevQuantity?: string;
  /**
   * Handle the selection of options
   * @param option
   * @returns
   */
  onOptionSelect: (option: string, price: number) => void;

  /**
   * Options for the dropdown. It can either be an array of strings or numbers
   */
  options: string[] | number[];
}

const MobileFlexi = ({
  quantity,
  prevQuantity,
  onOptionSelect,
  options,
  price,
  priceLoading,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [dynamicPrice, setDynamicPrice] = useState(price.price);
  const [selected, setSelected] = useState("0");
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerScrolled, setContainerScrolled] = useState(
    !Boolean(price.info)
  );

  const t = useTranslations("EventPage");
  const isShopsite = useIsShopsite();
  const locale = useLocale();

  const variants: Variants = {
    hidden: {
      y: isShopsite ? 200 : "100%",
      backdropFilter: isShopsite ? "" : "blur(64px) opacity(0)",
      opacity: 0,
    },
    show: {
      y: 0,
      backdropFilter: isShopsite ? "" : "blur(64px) opacity(1)",
      opacity: 1,
    },
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (container.scrollHeight <= container.clientHeight) {
      setContainerScrolled(true);
    }

    const scrollHandler = (event: any) => {
      const { scrollHeight, scrollTop, clientHeight } = event.target;

      setContainerScrolled(
        Math.abs(scrollHeight - clientHeight - scrollTop) < 1
      );
    };

    container.addEventListener("scroll", scrollHandler);

    return () => {
      container.removeEventListener("scroll", scrollHandler);
    };
  }, [open]);

  const isDisabled =
    price.status !== PriceStatus.AVAILABLE && parseInt(quantity || "0") === 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="relative">
          <Card
            asChild
            size="2"
            sharedClasses={joinClassNames(
              "w-full relative flex flex-col gap-2",
              priceLoading?.status === "loading" ? "overflow-hidden" : ""
            )}
            whitelabelClasses="disabled:bg-[color:var(--gray-1)]"
            shopsiteClasses={joinClassNames(
              "px-4 py-2 border shadow rounded-xl transition-all duration-150",
              isDisabled
                ? "border-gray-300 bg-gray-100"
                : "bg-white border-gray-200"
            )}
          >
            <motion.button
              whileTap={
                !isDisabled
                  ? {
                      scale: 0.95,
                    }
                  : undefined
              }
              disabled={isDisabled}
            >
              <AnimatePresence>
                {priceLoading?.status === "loading" && (
                  <motion.div
                    className="absolute top-0 left-0 w-[200%] h-full"
                    style={{
                      backgroundImage: `repeating-linear-gradient( 105deg,#9ca3af44,
                        #9ca3af44 7%,
                        #4b556344 7%,
                        #4b556344 10%
                      )`,
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ x: ["0%", "-50%"], opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      repeat: Infinity,
                      ease: "linear",
                      duration: 3,
                      opacity: {
                        duration: 0.1,
                      },
                    }}
                  />
                )}
              </AnimatePresence>
              <AnimatePresence>
                {(priceLoading?.status === "success" ||
                  priceLoading?.status === "failure") &&
                  quantity && (
                    <motion.div
                      className={joinClassNames(
                        "absolute top-0 left-0 z-10 flex items-center justify-center w-full h-full rounded-xl",
                        priceLoading.status === "success"
                          ? isShopsite
                            ? "bg-green-100"
                            : "bg-[color:var(--green-3)]"
                          : isShopsite
                          ? "bg-red-100"
                          : "bg-[color:var(--red-3)]"
                      )}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {parseInt(quantity) - parseInt(prevQuantity || "0") > 0 &&
                      priceLoading.status === "success" ? (
                        <div
                          className={joinClassNames(
                            "flex items-center gap-2",
                            isShopsite
                              ? "text-green-800"
                              : "text-[color:var(--green-11)]"
                          )}
                        >
                          <ShoppingCartIcon className="h-7 w-7" />
                          <Text
                            as="span"
                            size="3"
                            weight="bold"
                            shopsiteClasses="text-lg font-semibold"
                          >
                            {t("gaselection.addedtocart")}
                          </Text>
                        </div>
                      ) : parseInt(quantity) - parseInt(prevQuantity || "0") <
                          0 && priceLoading.status === "success" ? (
                        <div
                          className={joinClassNames(
                            "flex items-center gap-2",
                            isShopsite
                              ? "text-green-800"
                              : "text-[color:var(--green-11)]"
                          )}
                        >
                          <ShoppingCartIcon className="h-7 w-7" />
                          <Text
                            as="span"
                            size="3"
                            weight="bold"
                            shopsiteClasses="text-lg font-semibold"
                          >
                            {t("gaselection.removedfromcart")}
                          </Text>
                        </div>
                      ) : (
                        <Text
                          as="span"
                          size="3"
                          weight="bold"
                          whitelabelClasses="text-[color:var(--red-11)]"
                          shopsiteClasses="text-red-800"
                        >
                          {t("gaselection.somethingwentwrong")}
                        </Text>
                      )}
                    </motion.div>
                  )}
              </AnimatePresence>
              <div className="flex flex-row items-center justify-between gap-2 w-full">
                <Text
                  as="span"
                  color={isDisabled ? "gray" : undefined}
                  align="left"
                  size="4"
                  weight="bold"
                  shopsiteClasses={joinClassNames(
                    "text-left text-lg font-semibold",
                    isDisabled ? "text-gray-400" : ""
                  )}
                >
                  {price.type}
                </Text>
                <span
                  className={joinClassNames(
                    "flex items-center bg-iticket text-white text-xs italic font-semibold rounded-md px-2 py-1 -mr-2 shrink-0",
                    isDisabled ? "opacity-50" : ""
                  )}
                >
                  {formatPrice(
                    locale,
                    price.currency.CurrencyCode,
                    price.priceMinimum,
                    true
                  )}{" "}
                  -{" "}
                  {formatPrice(
                    locale,
                    price.currency.CurrencyCode,
                    price.priceMaximum,
                    true
                  )}
                </span>
              </div>
              {(isDisabled ||
                (price.bookingLimit ?? price.max) < price.min) && (
                <>
                  {price.status === PriceStatus.UPCOMING ? (
                    <Badge
                      color="amber"
                      sharedClasses="w-fit"
                      shopsiteClasses="!rounded-lg font-semibold"
                    >
                      <Text size="1" weight="bold">
                        {t("gaselection.notavailable")}
                      </Text>
                    </Badge>
                  ) : price.status === PriceStatus.AWAITINGTRIGGER ? (
                    <Badge
                      color="purple"
                      sharedClasses="w-fit"
                      shopsiteClasses="!rounded-lg font-semibold"
                    >
                      <Text size="1" weight="bold">
                        {t("gaselection.awaitingtrigger")}
                      </Text>
                    </Badge>
                  ) : (
                    <Badge
                      color="red"
                      sharedClasses="w-fit"
                      shopsiteClasses="!rounded-lg font-semibold"
                    >
                      <Text size="1" weight="bold">
                        {price.status === PriceStatus.NOTAVAILABLE
                          ? t("gaselection.notavailable")
                          : price.status === PriceStatus.SOLDOUT
                          ? t("gaselection.soldout")
                          : price.status === PriceStatus.BOOKEDOUT
                          ? t("gaselection.bookedout")
                          : t("gaselection.limitreached")}
                      </Text>
                    </Badge>
                  )}
                </>
              )}
              {price.status === PriceStatus.UPCOMING &&
              (price.availableFrom || price.priceAgeStartDate) ? (
                <Badge
                  asChild
                  color="amber"
                  size="2"
                  sharedClasses="cursor-default"
                  shopsiteClasses="!rounded-lg truncate font-semibold w-fit"
                >
                  <Text
                    as="span"
                    size="2"
                    weight="bold"
                    sharedClasses="w-fit truncate"
                  >
                    {t("gaselection.availability", {
                      preposition: "from",
                      date: format(
                        parseISO(
                          price.availableFrom ?? price.priceAgeStartDate ?? ""
                        ),
                        "EEE dd MMM yyy"
                      ),
                    })}
                  </Text>
                </Badge>
              ) : (
                <></>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge
                  sharedClasses="!bg-iticket text-white"
                  shopsiteClasses="!rounded-md w-fit"
                >
                  <Text
                    size="1"
                    weight="bold"
                    shopsiteClasses="font-semibold text-xs"
                    sharedClasses="italic"
                  >
                    {t("flexi.flexi")}
                  </Text>
                </Badge>
                {price.areaName ? (
                  <Badge color="sky" shopsiteClasses="!rounded-md w-fit">
                    <Text
                      size="1"
                      weight="bold"
                      shopsiteClasses="font-semibold text-xs"
                    >
                      {price.areaName}
                    </Text>
                  </Badge>
                ) : (
                  <></>
                )}
                {price.priceSchemeName ? (
                  <Badge color="cyan" shopsiteClasses="!rounded-md w-fit">
                    <Text
                      size="1"
                      weight="bold"
                      shopsiteClasses="font-semibold text-xs"
                    >
                      {price.priceSchemeName}
                    </Text>
                  </Badge>
                ) : (
                  <></>
                )}
                {price.priceSectionName ? (
                  <Badge color="teal" shopsiteClasses="!rounded-md w-fit">
                    <Text
                      size="1"
                      weight="bold"
                      shopsiteClasses="font-semibold text-xs"
                    >
                      {price.priceSectionName}
                    </Text>
                  </Badge>
                ) : (
                  <></>
                )}
              </div>
              {/* Tap indicator - only show when ticket is available */}
              {!isDisabled && (
                <div className="flex items-center justify-end gap-1 text-iticket">
                  <Text
                    as="span"
                    size="1"
                    shopsiteClasses="text-xs font-medium text-iticket"
                  >
                    {t("gaselection.taptoadd")}
                  </Text>
                  <ChevronRightIcon className="h-4 w-4" />
                </div>
              )}
            </motion.button>
          </Card>
          <div
            className={joinClassNames(
              "absolute -top-2 -right-1.5 flex items-center gap-1 flex-wrap justify-end transition-opacity duration-500 ease-in-out ",
              priceLoading !== null ? "opacity-0" : "opacity-100"
            )}
          >
            <Badge
              variant="solid"
              sharedClasses={quantity === "0" ? "hidden" : ""}
              shopsiteClasses="!rounded-lg !gap-1 !text-xs"
            >
              <Text as="span" size="1" weight="medium">
                {t("amountincart", { amount: quantity ?? "" })}
              </Text>
            </Badge>
          </div>
          <motion.div
            className="absolute -top-2 -right-1 z-20"
            animate={
              price.priceAgeId &&
              priceLoading?.id === price.priceAgeId &&
              priceLoading?.status !== "loading"
                ? { scale: [0, 1, 1.2, 1, 0], rotate: [0, -3, -6, -9, -12] }
                : { scale: 0, rotate: 0 }
            }
            transition={{ duration: 1.5 }}
          >
            {priceLoading?.status === "success" ? (
              <SparklesIcon className="text-yellow-400 h-6 w-6" />
            ) : priceLoading?.status === "failure" ? (
              <XMarkIcon className="text-red-400 h-6 w-6" />
            ) : (
              <></>
            )}
          </motion.div>
          <motion.div
            className="absolute -bottom-2 -left-1 z-20"
            animate={
              price.priceAgeId &&
              priceLoading?.id === price.priceAgeId &&
              priceLoading?.status !== "loading"
                ? { scale: [0, 1, 1.2, 1, 0], rotate: [0, -3, -6, -9, -12] }
                : { scale: 0, rotate: 0 }
            }
            transition={{ duration: 1.5 }}
          >
            {priceLoading?.status === "success" ? (
              <SparklesIcon className="text-yellow-400 h-6 w-6" />
            ) : priceLoading?.status === "failure" ? (
              <XMarkIcon className="text-red-400 h-6 w-6" />
            ) : (
              <></>
            )}
          </motion.div>
          {priceLoading &&
          priceLoading.status !== "loading" &&
          price.priceAgeId &&
          priceLoading.id === price.priceAgeId ? (
            <>
              <motion.div
                className={joinClassNames(
                  "absolute -top-2 left-4 w-2.5 h-2.5 rounded-[2px] z-20",
                  priceLoading.status === "success"
                    ? "bg-emerald-200"
                    : "bg-red-200"
                )}
                initial={{ x: 0, y: 0, opacity: 0, rotate: 0 }}
                animate={{
                  x: [0, -4, -8, -14, -16, -18],
                  y: [0, -8, -10, -9, -5, 10],
                  opacity: [0, 1, 1, 1, 1, 0],
                  rotate: [-1, -3, -6, -9, -12],
                }}
                transition={{ duration: 1, delay: 0.1 }}
              />
              <motion.div
                className={joinClassNames(
                  "absolute -top-2 left-24 w-3.5 h-3.5 rounded z-20",
                  priceLoading.status === "success"
                    ? "bg-emerald-500"
                    : "bg-red-500"
                )}
                initial={{ x: 0, y: 0, scale: 0, rotate: 0 }}
                animate={{
                  x: [0, -2, -4, -5, -5],
                  y: [0, -2, -4, -5, -5],
                  scale: [0, 1, 1.2, 1, 0],
                  rotate: [0, -3, -6, -9, -12],
                }}
                transition={{ duration: 1.2 }}
              />
              <motion.div
                className={joinClassNames(
                  "absolute -top-4 right-20 w-5 h-5 rounded-[3px] z-20",
                  priceLoading.status === "success"
                    ? "bg-emerald-200"
                    : "bg-red-700"
                )}
                initial={{ x: 0, y: 0, scale: 0, rotate: 0 }}
                animate={{
                  x: [0, 2, 4, 5, 5],
                  y: [0, -2, -4, -5, -7, -7],
                  scale: [0, 1, 1.2, 1, 0],
                  rotate: [1, 6, 12, 18, 24],
                }}
                transition={{ duration: 1.5, delay: 0.2 }}
              />
              <motion.div
                className={joinClassNames(
                  "absolute -bottom-1 right-1/2 w-3.5 h-3.5 rounded-[3px] z-20",
                  priceLoading.status === "success"
                    ? "bg-emerald-500"
                    : "bg-red-500"
                )}
                initial={{ x: 0, y: 0, scale: 0, rotate: 0 }}
                animate={{
                  x: [0, -2, -4, -5, -5],
                  y: [0, 2, 4, 5, 7, 7],
                  scale: [0, 1, 1.2, 0.8, 0.4, 0],
                  rotate: [0, -3, -6, -9, -12],
                }}
                transition={{ duration: 1.5 }}
              />
              <motion.div
                className={joinClassNames(
                  "absolute -bottom-2 right-4 w-4 h-4 rounded-[3px] z-20",
                  priceLoading.status === "success"
                    ? "bg-emerald-500"
                    : "bg-red-500"
                )}
                initial={{ x: 0, y: 0, scale: 0, rotate: 0 }}
                animate={{
                  x: [0, 1, 2, 3, 3],
                  y: [0, 2, 4, 5, 5],
                  scale: [0, 1, 1.2, 1, 0],
                  rotate: [0, -3, -6, -9, -12],
                }}
                transition={{ duration: 1.5 }}
              />
            </>
          ) : (
            <></>
          )}
        </div>
      </DialogTrigger>
      <AnimatePresence>
        {open && (
          <DialogContent asChild forceMount handleClose={() => setOpen(false)}>
            <Card
              asChild
              shopsiteClasses="rounded-3xl !m-0"
              sharedClasses="flex flex-col !gap-0"
            >
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                transition={{ duration: 0.2 }}
                variants={variants}
              >
                <div
                  className={joinClassNames(
                    "relative flex flex-col gap-4",
                    isShopsite ? "px-6 pt-6 gap-4 bg-iticket" : "",
                    !price.info ? "pb-2" : ""
                  )}
                >
                  <div className="relative self-end w-8 h-8 rounded-full bg-white/10">
                    <DialogClose className="absolute top-2 left-2 w-4 h-4 text-white !font-bold">
                      <XMarkIconOutline />
                    </DialogClose>
                  </div>
                  <DialogHeader className="!text-left">
                    <DialogTitle className="!text-white text-xl !font-bold">
                      {price.type}
                    </DialogTitle>
                    <ArrowTrendingUpIcon
                      className={joinClassNames(
                        "absolute left-4 w-24 h-24 text-white/10 -rotate-12",
                        isShopsite ? "top-0" : "-top-6"
                      )}
                    />
                  </DialogHeader>
                  {price.info ? (
                    <span
                      className={joinClassNames(
                        "absolute top-full inset-x-0 w-full h-4 bg-gradient-to-b",
                        isShopsite ? "from-iticket" : "hidden"
                      )}
                    />
                  ) : (
                    <></>
                  )}
                </div>
                <div ref={containerRef} className="overflow-auto">
                  {price.info ? (
                    <div
                      className={joinClassNames(
                        "py-2",
                        isShopsite ? "bg-iticket px-6" : ""
                      )}
                    >
                      <Text color="gray" shopsiteClasses="text-white/75">
                        {price.info}
                      </Text>
                    </div>
                  ) : (
                    <></>
                  )}
                  <div
                    className={joinClassNames(
                      "flex flex-col items-center gap-4 w-full py-4 px-6",
                      isShopsite ? "bg-white" : ""
                    )}
                  >
                    <Text
                      as="span"
                      weight="bold"
                      whitelabelClasses="text-[color:var(--crimson-9)]"
                      shopsiteClasses="font-black text-iticket"
                      sharedClasses="relative w-fit leading-none text-7xl"
                    >
                      {formatPrice(
                        locale,
                        price.currency.CurrencyCode,
                        dynamicPrice ? dynamicPrice : price.price,
                        true
                      )}
                      <Text
                        as="span"
                        sharedClasses="absolute top-0 left-full text-base"
                      >
                        {t("flexi.each")}
                      </Text>
                    </Text>
                    <Slider
                      defaultValue={[dynamicPrice ? dynamicPrice : price.price]}
                      max={price.priceMaximum}
                      min={price.priceMinimum}
                      step={1}
                      onValueChange={(val) => {
                        setDynamicPrice(val[0]);
                      }}
                    />
                    <div className="flex items-center justify-between w-full">
                      <Text as="span">
                        {formatPrice(
                          locale,
                          price.currency.CurrencyCode,
                          price.priceMinimum,
                          true
                        )}
                      </Text>
                      <Text as="span">
                        {formatPrice(
                          locale,
                          price.currency.CurrencyCode,
                          price.priceMaximum,
                          true
                        )}
                      </Text>
                    </div>
                  </div>
                  <div
                    className={joinClassNames(
                      "max-h-[calc(75dvh-108px-150px-76px)]",
                      isShopsite ? "bg-white" : "",
                      containerScrolled ? "overflow-y-auto" : "overflow-hidden"
                    )}
                  >
                    {options.map((option) => (
                      <button
                        onClick={() => {
                          containerRef.current?.scrollTo({
                            top: containerRef.current.scrollHeight,
                            behavior: "smooth",
                          });
                          setSelected(option.toString());
                        }}
                        key={option}
                        className="flex flex-col items-center w-full"
                      >
                        <div className="relative flex items-center">
                          {option.toString() === selected ? (
                            <CheckIcon
                              className={joinClassNames(
                                "absolute right-[calc(100%-1rem)] top-2 w-7 h-7",
                                isShopsite
                                  ? "text-iticket stroke-[3]"
                                  : "text-[color:var(--crimson-a11)] stroke-2"
                              )}
                            />
                          ) : (
                            <></>
                          )}
                          <Text
                            size="6"
                            weight="bold"
                            color={
                              option.toString() === selected
                                ? "crimson"
                                : undefined
                            }
                            sharedClasses="py-2 px-6 w-fit"
                            shopsiteClasses={joinClassNames(
                              "text-2xl font-black",
                              option.toString() === selected
                                ? "text-iticket"
                                : "text-black"
                            )}
                          >
                            {option}
                          </Text>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div
                    className={joinClassNames(
                      "pt-3",
                      isShopsite ? "px-6 pb-6 bg-white" : ""
                    )}
                  >
                    <Button
                      asChild
                      size="3"
                      color="crimson"
                      disabled={!!priceLoading || selected === "0"}
                      shopsiteVariant="iticket"
                      shopsiteClasses="gap-2"
                      sharedClasses="w-full"
                    >
                      <motion.button
                        whileTap={
                          !priceLoading && selected !== "0"
                            ? {
                                scale: 0.95,
                              }
                            : undefined
                        }
                        onClick={async () => {
                          if (selected) {
                            onOptionSelect(selected, dynamicPrice);
                            setSelected("0");
                            setOpen(false);
                          }
                        }}
                      >
                        {!!priceLoading ? (
                          <>
                            <LoadingSpinner size="20" color="#ffffff" />
                            <span>{t("flexi.addingtocart")}</span>
                          </>
                        ) : (
                          <>
                            <ShoppingCartIcon className="w-5 h-5" />
                            <span>{t("flexi.addtocart")}</span>
                          </>
                        )}
                      </motion.button>
                    </Button>
                  </div>
                </div>
                <span
                  className={joinClassNames(
                    "absolute bottom-0 inset-x-0 w-full h-4 bg-gradient-to-t from-white",
                    isShopsite ? "" : "hidden"
                  )}
                />
              </motion.div>
            </Card>
          </DialogContent>
        )}
      </AnimatePresence>
    </Dialog>
  );
};

export default MobileFlexi;
